import * as yup from 'yup';
import SignaturePad from 'react-signature-canvas';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRef, useMemo, useState, useEffect } from 'react';

import Radio from '@mui/material/Radio';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import TextField from '@mui/material/TextField';
import RadioGroup from '@mui/material/RadioGroup';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import FormHelperText from '@mui/material/FormHelperText';
import FormControlLabel from '@mui/material/FormControlLabel';

import { isValidJSON } from 'src/utils/function';

import { formsApi } from 'src/api/forms';
import { useLocales } from 'src/locales';

import Iconify from 'src/components/iconify';
import { Upload } from 'src/components/upload';
import Scrollbar from 'src/components/scrollbar';
import { useSnackbar } from 'src/components/snackbar';
import { LeftIcon, RightIcon } from 'src/components/carousel/arrow-icons';

const getFileData = (file) => {
  if (!file) return null;

  return {
    preview: file?.type?.startsWith('image/')
      ? URL.createObjectURL(file)
      : '/assets/icons/files/ic_pdf.svg',
    name: file?.name,
  };
};

export const FormModal = ({
  formId = -1,
  settings,
  formName,
  open,
  onClose,
  currentFormIndex,
  formListCount,
  handleNextForm,
  handlePrevForm,
  handleSubmittedForm,
}) => {
  const brandId = localStorage.getItem('brandId');

  const { currentLang } = useLocales();

  const formNameDesInfo = useMemo(()=> {
    let formNameInfo = {};
    if (isValidJSON(formName)) {
      const parsedFormName = JSON.parse(formName);
      formNameInfo = {...parsedFormName};
    } else {
      formNameInfo.en = formName;
    }
    return { 
      name: formNameInfo?.[currentLang?.value] ?? formNameInfo?.en ?? "" , 
    }
  
  }, [formName, currentLang])

  const [signatures, setSignatures] = useState({});
  const [isSubmited, setIsSubmited] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState({});

  const sigCanvas = useRef({});
  const { enqueueSnackbar } = useSnackbar();

  const validationObject = useMemo(() => {
    let yupObj = {};
    settings
      ?.filter((item) => !item?.isOptional && item.inputType !== 5 && item.inputType !== 6)
      ?.forEach((item) => {
        yupObj = {
          ...yupObj,
          [item?.id]:
            item?.inputType !== 3
              ? yup.string().required('This field is required.')
              : yup
                  .array()
                  .min(1, 'This field is required.')
                  .of(yup.string().required('Item is required')),
        };
      });
    return yupObj;
  }, [settings]);

  const validationSchema = yup.object({
    ...validationObject,
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
    setValue,
    setError,
    clearErrors,
    watch,
  } = useForm({ resolver: yupResolver(validationSchema) });

  useEffect(() => {
    if (settings?.length) {
      settings
        ?.filter((item) => !item?.isOptional)
        ?.forEach((item) => {
          setValue(item.id, item?.inputType !== 3 ? '' : []);
        });
    } else handlePrevForm();
  }, [settings]);

  const onSubmit = (data) => {
    setIsSubmited(true);

    const formData = new FormData();
    formData.append('internal_brand_id', brandId);
    formData.append('brand_form_id', formId);

    const result = settings
      ?.filter((setting) => setting.inputType !== 5 && setting.inputType !== 6)
      .map((setting) => ({
        ...setting,
        value: setting.inputType === 4 ? signatures[setting.id] ?? '' : data[setting.id] ?? '',
        isShowCopy: setting?.isShowCopy,
        form_id: formId,
      }));

    formData.append('settings', JSON.stringify(result));

    settings
      ?.filter((setting) => setting.inputType === 5)
      ?.forEach((setting, index) => {
        const file = uploadedFiles[setting.id];
        if (file) {
          formData.append(`form_docs[${index}][name]`, setting.name);
          formData.append(`form_docs[${index}][binary]`, file);
        }
      });

    setIsSubmitting(true);

    formsApi
      .submitForm(formData)
      .then(() => {
        handleSubmittedForm(formId);
        enqueueSnackbar('Form is submitted successfully.', { variant: 'success' });
      })
      .catch(() => {
        enqueueSnackbar('Something went wrong.', { variant: 'error' });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  useEffect(
    () => () => {
      settings
        ?.filter((setting) => setting.inputType === 5)
        ?.forEach((setting) => {
          const value = watch(setting.id);
          const file = getFileData(value);
          if (file?.preview && !file.preview.includes('ic_pdf.svg')) {
            URL.revokeObjectURL(file.preview);
          }
        });
    },
    [settings, watch]
  );

  const onSubmitErrorHandler = () => {
    setIsSubmited(true);
    settings?.forEach((setting) => {
      if (!setting?.isOptional && (setting.inputType === 4 || setting.inputType === 3)) {
        if (!signatures[setting.id]) {
          setError(setting.id, { type: 'custom', message: 'The field is required' });
        } else {
          clearErrors(setting.id);
          setValue(setting.id, '_');
        }
      }
    });
  };

  useEffect(() => {
    settings.forEach((setting) => {
      if (setting.inputType === 4 && !setting?.isOptional) {
        if (!signatures[setting.id]) {
          setValue(setting.id, '');
        } else {
          clearErrors(setting.id);
          setValue(setting.id, '_');
        }
      }
    });
  }, [signatures, isSubmited, settings]);

  const onClearCanvas = (id) => {
    if (signatures[id]) {
      sigCanvas.current[id].clear();
      setSignatures((prev) => ({ ...prev, [id]: null }));
      setError(id, { type: 'custom', message: 'The field is required' });
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    enqueueSnackbar('Copied to clipboard', { variant: 'success' });
  };

  const onSaveCanvas = (id) => {
    setSignatures((prev) => ({ ...prev, [id]: sigCanvas.current[id].toDataURL() }));
  };

  useEffect(() => {
    setIsSubmited(false);
    setSignatures({});
    reset();
  }, [formId]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth className="form-modal">
      <Stack sx={{ px: 4, minHeight: 'none', justifyContent: 'space-between' }} spacing={2}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={2}
          sx={{ mt: 8, mb: 1 }}
        >
          <Stack alignItems="flex-end">
            <Typography variant="h4">Fill the forms and submit</Typography>
          </Stack>
        </Stack>
        <form onSubmit={handleSubmit(onSubmit, onSubmitErrorHandler)} style={{ height: '100%' }}>
          <Stack direction="column" sx={{ minHeight: 480, flexGrow: 1, mx: 2, pb: 2 }}>
            <Typography variant="h5" px={1}>
              {formNameDesInfo?.name ?? ''}
            </Typography>
            <Scrollbar sx={{ maxHeight: 450, px: 1 }}>
              {settings?.map((setting) => {
                let settingNameInfo = {};
                if (isValidJSON(setting?.name)) {
                  const parsedForm = JSON.parse(setting?.name);
                  settingNameInfo = {...parsedForm};
                } else {
                  settingNameInfo.en = setting?.name;
                }

                return (
                  <Stack key={setting.id} spacing={0.5}>
                    {setting.inputType !== 6 && (
                      <Typography variant="subtitle2" sx={{ mt: 3 }}>
                        {settingNameInfo?.[currentLang.value]?.settingNameInfo?.[currentLang.value] > 0 ? settingNameInfo?.[currentLang.value] : settingNameInfo?.en ?? ""}
                      </Typography>
                    )}
                    {setting.inputType === 1 && (
                      <TextField
                        variant="standard"
                        sx={{ maxWidth: '100%', width: 1 }}
                        {...register(setting?.id)}
                        error={!!errors?.[setting?.id]?.message}
                        helperText={errors?.[setting?.id]?.message} />
                    )}
                    {setting.inputType === 2 && (
                      <Stack key={setting.id}>
                        <Controller
                          control={control}
                          name={setting?.id}
                          render={({ field: { onChange } }) => (
                            <RadioGroup
                              sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}
                              name={setting.id}
                              onChange={(e) => onChange(e.target.value)}
                            >
                              {setting.options.map((optionItem) => {
                                let settingOptionInfo = {};
  
                                if (isValidJSON(optionItem?.option)) {
                                  const parsedForm = JSON.parse(optionItem?.option);
                                  settingOptionInfo = {...parsedForm};
                                } else {
                                  settingOptionInfo.en = optionItem?.option;
                                }
                                return (
                                  <FormControlLabel
                                    key={optionItem.id}
                                    control={
                                      <Radio
                                        size="normal"
                                        value={optionItem.id}
                                        sx={{ '& .MuiSvgIcon-root': { fontSize: 24 } }} />}
                                        label={settingOptionInfo?.[currentLang.value]?.length > 0 ? settingOptionInfo?.[currentLang.value] : settingOptionInfo?.en ?? ""} 
                                      />
                                );
                              })}
                            </RadioGroup>
                          )} />
                        {errors?.[setting?.id]?.message ? (
                          <FormHelperText error={!!errors?.[setting?.id]?.message} sx={{ m: 0 }}>
                            {errors?.[setting?.id]?.message ?? ''}
                          </FormHelperText>
                        ) : null}
                      </Stack>
                    )}
                    {setting.inputType === 3 && (
                      <Stack key={setting.id}>
                        <FormGroup>
                          <Stack direction="row" gap={0.5} sx={{ flexWrap: 'wrap' }}>
                            <Controller
                              control={control}
                              name={setting?.id}
                              render={({ field: { value = [], onChange } }) => (
                                <>
                                  {setting.options.map((optionItem) => {
                                    let settingOptionInfo = {};
                                    
                                    if (isValidJSON(optionItem?.option)) {
                                      const parsedForm = JSON.parse(optionItem?.option);
                                      settingOptionInfo = {...parsedForm};
                                    } else {
                                      settingOptionInfo.en = optionItem?.option;
                                    }
                                    return (
                                      <FormControlLabel
                                        key={optionItem.id}
                                        control={<Checkbox
                                        size="medium"
                                        checked={value?.includes(optionItem?.id)}
                                        onChange={(e) => {
                                          const newValue = e.target.checked
                                            ? [...value, optionItem.id]
                                            : value?.filter((v) => v !== optionItem.id);
                                          onChange(newValue);
                                        } } />}
                                        label={settingOptionInfo?.[currentLang.value]?.length > 0 ? settingOptionInfo?.[currentLang.value] : settingOptionInfo?.en ?? ""} 
                                      />
                                    );
                                  })}
                                </>
                              )} />
                          </Stack>
                        </FormGroup>
                        {errors?.[setting?.id]?.message ? (
                          <FormHelperText error={!!errors?.[setting?.id]?.message} sx={{ m: 0 }}>
                            {errors?.[setting?.id]?.message ?? ''}
                          </FormHelperText>
                        ) : null}
                      </Stack>
                    )}
                    {setting.inputType === 4 && (
                      <Stack>
                        <Stack
                          sx={{
                            position: 'relative',
                            border: errors?.[setting?.id]?.message
                              ? 'solid 1px #EF4444'
                              : 'solid 1px #eee',
                            backgroundColor: '#FFF',
                          }}
                        >
                          <SignaturePad
                            ref={(el) => {
                              sigCanvas.current[setting.id] = el;
                            } }
                            onEnd={() => onSaveCanvas(setting.id)} />
                          {signatures[setting.id] && (
                            <IconButton
                              onClick={() => onClearCanvas(setting.id)}
                              sx={{ position: 'absolute', right: 0, bottom: 0 }}
                            >
                              <Iconify icon="mingcute:eraser-line" width={24} />
                            </IconButton>
                          )}
                        </Stack>
                        {errors?.[setting?.id]?.message ? (
                          <FormHelperText error={!!errors?.[setting?.id]?.message} sx={{ m: 0 }}>
                            {errors?.[setting?.id]?.message ?? ''}
                          </FormHelperText>
                        ) : null}
                      </Stack>
                    )}
                    {setting.inputType === 5 && (
                      <Controller
                        name={setting.id}
                        control={control}
                        render={({ field: { value }, fieldState: { error } }) => {
                          const fileData = getFileData(uploadedFiles[setting.id]);

                          return (
                            <>
                              {fileData ? (
                                <Stack
                                  spacing={2}
                                  sx={{
                                    p: 2,
                                    bgcolor: 'background.paper',
                                    borderRadius: 1,
                                    border: error
                                      ? '1px solid #FF4842'
                                      : '1px solid rgba(145, 158, 171, 0.2)',
                                  }}
                                >
                                  <Stack direction="row" alignItems="center" spacing={2}>
                                    {fileData.preview && (
                                      <Stack
                                        component="img"
                                        src={fileData.preview}
                                        sx={{
                                          width: 48,
                                          height: 48,
                                          borderRadius: 1,
                                          objectFit: 'cover',
                                          flexShrink: 0,
                                        }} />
                                    )}

                                    <Stack spacing={0.5} flexGrow={1}>
                                      <Typography variant="subtitle2">{fileData.name}</Typography>
                                    </Stack>

                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setUploadedFiles((prev) => ({
                                          ...prev,
                                          [setting.id]: null,
                                        }));
                                        setValue(setting.id, null);
                                      } }
                                    >
                                      <Iconify icon="eva:close-fill" />
                                    </IconButton>
                                  </Stack>
                                </Stack>
                              ) : (
                                <Upload
                                  file={value}
                                  accept={{ 'image/*': [], 'application/pdf': [] }}
                                  maxSize={3145728}
                                  onDrop={(acceptedFiles) => {
                                    const file = acceptedFiles[0];
                                    if (file) {
                                      setUploadedFiles((prev) => ({
                                        ...prev,
                                        [setting.id]: file,
                                      }));
                                      setValue(setting.id, file.name);
                                    }
                                  } }
                                  sx={{
                                    bgcolor: 'background.paper',
                                    borderRadius: 1,
                                    p: 2,
                                    border: error
                                      ? '1px solid #FF4842'
                                      : '1px solid rgba(145, 158, 171, 0.2)',
                                  }} />
                              )}

                              {error && (
                                <FormHelperText error sx={{ px: 1 }}>
                                  {error.message}
                                </FormHelperText>
                              )}
                            </>
                          );
                        } } />
                    )}
                    {setting.inputType === 6 && (
                      <Stack
                        sx={{
                          bgcolor: 'background.paper',
                          borderRadius: 1,
                          pt: 2,
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                          {settingNameInfo?.[currentLang.value]?.length > 0 ? settingNameInfo?.[currentLang.value] : settingNameInfo?.en ?? ""}
                        </Typography>

                        <Stack spacing={2}>
                          {setting?.options?.map((optionItem) => {
                            let settingOptionInfo = {};
  
                            if (isValidJSON(optionItem?.option)) {
                              const parsedForm = JSON.parse(optionItem?.option);
                              settingOptionInfo = {...parsedForm};
                            } else {
                              settingOptionInfo.en = optionItem?.option;
                            }
                            return (
                              <Stack
                                key={optionItem.id}
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{
                                  p: 1.5,
                                  borderRadius: 1,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  '&:hover': {
                                    bgcolor: 'background.neutral',
                                  },
                                }}
                              >
                                <Typography variant="body2">{settingOptionInfo?.[currentLang.value]?.length > 0 ? settingOptionInfo?.[currentLang.value] : settingOptionInfo?.en ?? ""}</Typography>

                                {setting?.isShowCopy && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleCopy(settingOptionInfo?.[currentLang.value]?.length > 0 ? settingOptionInfo?.[currentLang.value] : settingOptionInfo?.en ?? "")}
                                    sx={{
                                      color: 'text.secondary',
                                      '&:hover': {
                                        color: 'primary.main',
                                      },
                                    }}
                                  >
                                    <Iconify icon="solar:copy-linear" width={20} />
                                  </IconButton>
                                )}
                              </Stack>
                            );
                          })}
                        </Stack>
                      </Stack>
                    )}
                  </Stack>
                );
              })}
            </Scrollbar>
          </Stack>
          <Stack justifySelf="center">
            <LoadingButton
              loading={isSubmitting}
              color="primary"
              variant="contained"
              type="submit"
              sx={{ width: 150, ml: 'auto' }}
            >
              Submit
            </LoadingButton>
          </Stack>
        </form>
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={1}
          sx={{ mb: 4 }}
        >
          <Button onClick={handlePrevForm} disabled={currentFormIndex === 0}>
            <LeftIcon />
          </Button>
          {`${currentFormIndex + 1} / ${formListCount}`}
          <Button onClick={handleNextForm} disabled={currentFormIndex >= formListCount - 1}>
            <RightIcon />
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
};
